import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TablePagination,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridRowSelectionModel,
} from '@mui/x-data-grid';
import { bulkDeleteNominations, listNominations, type Nomination } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { WAF_YELLOW } from '../theme';
import { WafDataGrid } from '../components/WafDataGrid';
import { LoadingScreen } from '../components/LoadingScreen';
import { EntryFormDialog } from '../components/EntryFormDialog';

// -v2 suffix: default flipped to 'grid', so ignore any stale 'table' pref saved
// under the old key and let everyone pick up the new grid default.
const VIEW_MODE_KEY = 'waf-entries-view-mode-v2';
const PAGE_SIZE_KEY = 'waf-entries-page-size';
const PAGE_SIZE_OPTIONS = [10, 30, 50, 100];

function useDebounced<T>(value: T, ms = 150): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

interface Row extends Nomination {
  id: string;
  imageCount: number;
}

export function EntryList() {
  const navigate = useNavigate();
  // Only staff (Admin/User) may mutate. The read-only PR role gets a view-only
  // grid: no add/edit/duplicate/delete controls, no row selection.
  const { hasRole } = useAuth();
  const canEdit = hasRole('Admin') || hasRole('User');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [year, setYear] = useState<string>('all');
  const [category, setCategory] = useState<string>(searchParams.get('category') ?? 'all');
  const [shortlisted, setShortlisted] = useState<'all' | 'yes' | 'no'>('all');
  // Create / duplicate dialog. source != null => duplicate; null => add new.
  const [formOpen, setFormOpen] = useState(false);
  const [formSource, setFormSource] = useState<Row | null>(null);
  const debouncedQuery = useDebounced(query, 150);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [deleteMode, setDeleteMode] = useState<'soft' | 'hard'>('soft');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem(VIEW_MODE_KEY) : null;
    return saved === 'table' ? 'table' : 'grid';
  });

  useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  // Paginate both views — the grid otherwise renders every card (and fires a
  // hero-image request each), which is slow at hundreds of entries. Page size
  // is remembered; default 30.
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = typeof window !== 'undefined' ? Number(window.localStorage.getItem(PAGE_SIZE_KEY)) : NaN;
    return PAGE_SIZE_OPTIONS.includes(saved) ? saved : 30;
  });
  const [page, setPage] = useState(0);

  useEffect(() => {
    window.localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
  }, [pageSize]);

  // Snap to the first page when the filtered set changes so you're never
  // stranded on a now-empty page.
  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, year, category, shortlisted]);


  const refresh = () => {
    setLoading(true);
    return listNominations()
      .then((data) => {
        setRows(data.map((n) => ({ ...n, id: n.nomination_uuid, imageCount: 0 })));
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const onConfirmDelete = async () => {
    if (selection.length === 0) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await bulkDeleteNominations(selection as string[], deleteMode);
      setDeleteDialog(false);
      setSelection([]);
      await refresh();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'delete failed');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    listNominations()
      .then((data) => {
        if (cancelled) return;
        setRows(
          data.map((n) => ({
            ...n,
            id: n.nomination_uuid,
            imageCount: 0, // populated lazily when opening an entry
          }))
        );
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const years = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.year && set.add(String(r.year)));
    return Array.from(set).sort().reverse();
  }, [rows]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.category_name && set.add(r.category_name));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (year !== 'all' && String(r.year) !== year) return false;
      if (category !== 'all' && r.category_name !== category) return false;
      if (shortlisted === 'yes' && !r.shortlisted_at) return false;
      if (shortlisted === 'no' && r.shortlisted_at) return false;
      if (!q) return true;
      return (
        (r.project_title || '').toLowerCase().includes(q) ||
        (r.architect_company_name || '').toLowerCase().includes(q) ||
        (r.lead_architect_name || '').toLowerCase().includes(q) ||
        (r.project_country || '').toLowerCase().includes(q) ||
        (r.architect_company_country || '').toLowerCase().includes(q) ||
        (r.category_name || '').toLowerCase().includes(q) ||
        (r.nomination_code || '').toLowerCase().includes(q)
      );
    });
  }, [rows, debouncedQuery, year, category, shortlisted]);

  // Asset counts drive the quick-status columns. Sort works off valueGetter so
  // clicking a header orders by "fewest assets first" — i.e. entries missing
  // images/hero/presentation bubble to the top.
  function imgCount(row: Row): number {
    const r = row as Record<string, unknown>;
    let n = 0;
    for (let i = 2; i <= 10; i++) if (r[`image_${i}`]) n++;
    return n;
  }
  function imgColor(n: number): string {
    if (n === 0) return '#EF4444';
    if (n < 5) return '#F59E0B';
    return '#22C55E';
  }
  function a2Count(row: Row): number {
    const r = row as Record<string, unknown>;
    return (r.a2board1 ? 1 : 0) + (r.a2board2 ? 1 : 0);
  }
  function hasVideo(row: Row): boolean {
    const r = row as Record<string, unknown>;
    return !!(r.video_landscape_url || r.video_portrait_url || r.video_download_url);
  }

  const columns: GridColDef<Row>[] = [
    {
      field: 'nomination_code',
      headerName: 'Project Number',
      width: 160,
      sortable: true,
    },
    {
      // Fixed cap — titles can run to full sentences on some entries;
      // letting this flex pushes later columns off-screen. Ellipsis +
      // tooltip preserve the full text on hover.
      field: 'project_title',
      headerName: 'Title',
      width: 300,
    },
    {
      // Same cap applies — architect/company names can be long,
      // especially non-ASCII ones.
      field: 'architect_company_name',
      headerName: 'Architect',
      width: 240,
    },
    {
      field: 'architect_company_country',
      headerName: 'Architect country',
      width: 150,
    },
    {
      field: 'project_country',
      headerName: 'Project country',
      width: 140,
    },
    {
      field: 'category_name',
      headerName: 'Category',
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'shortlisted',
      headerName: 'Shortlisted',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_v, row) => (row.shortlisted_at ? 1 : 0),
      renderCell: (p: GridRenderCellParams<Row>) =>
        p.row.shortlisted_at ? (
          <Chip size="small" color="success" label="Shortlisted" />
        ) : (
          <Typography variant="caption" color="text.disabled">—</Typography>
        ),
    },
    {
      field: 'hasHero',
      headerName: 'Hero',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_v, row) => (row.hero_image ? 1 : 0),
      renderCell: (p: GridRenderCellParams<Row>) => {
        const has = !!p.row.hero_image;
        return (
          <Tooltip title={has ? 'Hero image uploaded' : 'No hero image'}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: has ? '#22C55E' : '#EF4444' }} />
          </Tooltip>
        );
      },
    },
    {
      field: 'imgCount',
      headerName: 'Images',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_v, row) => imgCount(row),
      renderCell: (p: GridRenderCellParams<Row>) => {
        const n = imgCount(p.row);
        return (
          <Tooltip title={`${n} of 9 project images`}>
            <Typography variant="caption" sx={{ color: imgColor(n), fontWeight: 700 }}>
              {n}/9
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'a2boards',
      headerName: 'A2',
      width: 60,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_v, row) => a2Count(row),
      renderCell: (p: GridRenderCellParams<Row>) => {
        const n = a2Count(p.row);
        const color = n === 2 ? '#22C55E' : n === 1 ? '#F59E0B' : '#EF4444';
        return (
          <Tooltip title={n === 2 ? 'Both A2 boards' : n === 1 ? '1 of 2 A2 boards' : 'No A2 boards'}>
            <Typography variant="caption" sx={{ color, fontWeight: 700 }}>{n}/2</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'hasPres',
      headerName: 'Pres',
      width: 60,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_v, row) => ((row as Record<string, unknown>).presentation_uploaded ? 1 : 0),
      renderCell: (p: GridRenderCellParams<Row>) => {
        const has = !!(p.row as Record<string, unknown>).presentation_uploaded;
        return (
          <Tooltip title={has ? 'Presentation uploaded' : 'No presentation'}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: has ? '#22C55E' : '#9CA3AF' }} />
          </Tooltip>
        );
      },
    },
    {
      field: 'hasVideo',
      headerName: 'Video',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_v, row) => (hasVideo(row) ? 1 : 0),
      renderCell: (p: GridRenderCellParams<Row>) => {
        const has = hasVideo(p.row);
        return (
          <Tooltip title={has ? 'Video linked' : 'No video'}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: has ? '#22C55E' : '#9CA3AF' }} />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: canEdit ? 150 : 60,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Row>) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Open">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/entry/${params.row.nomination_uuid}`);
              }}
            >
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Edit + duplicate are mutation entry-points — staff only. */}
          {canEdit && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/entry/${params.row.nomination_uuid}?edit=1`);
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title="Duplicate (e.g. for a second category)">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setFormSource(params.row);
                  setFormOpen(true);
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  const titleYear = year === 'all' ? '' : ` - ${year}`;

  // First load (no data yet): show the branded loading screen rather than an
  // empty table/grid. Once any rows have arrived, subsequent reloads use the
  // in-place table/grid loading indicators instead.
  if (loading && rows.length === 0) {
    return <LoadingScreen label="Loading entries…" />;
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h3" component="h1" align="center">
        View all entries{titleYear}
      </Typography>

      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setFormSource(null); setFormOpen(true); }}
          >
            Add entry
          </Button>
        </Box>
      )}

      <Paper sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          {/* Search + filters — search grows to fill so its placeholder fits. */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <TextField
              size="small"
              placeholder="Search title, architect, country, category…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ flex: 1, minWidth: { sm: 340 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              size="small"
              select
              label="Year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              sx={{ width: { xs: '100%', sm: 150 }, flexShrink: 0 }}
            >
              <MenuItem value="all">All years</MenuItem>
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              sx={{ width: { xs: '100%', sm: 230 }, flexShrink: 0 }}
            >
              <MenuItem value="all">All categories</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Shortlisted"
              value={shortlisted}
              onChange={(e) => setShortlisted(e.target.value as 'all' | 'yes' | 'no')}
              sx={{ width: { xs: '100%', sm: 170 }, flexShrink: 0 }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Shortlisted</MenuItem>
              <MenuItem value="no">Not shortlisted</MenuItem>
            </TextField>
          </Stack>
          {/* View toggle + result count, pinned to the right. */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent={{ xs: 'space-between', md: 'flex-start' }}
            sx={{ flexShrink: 0 }}
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={(_e, v) => v && setViewMode(v)}
            >
              <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Table view"><ViewListOutlinedIcon fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="grid" aria-label="Grid view">
                <Tooltip title="Grid view"><GridViewOutlinedIcon fontSize="small" /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {filtered.length} of {rows.length}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {viewMode === 'table' ? (
        <Paper
          sx={{
            // Break out of the Layout's 1200px max-width wrapper so the grid
            // can use 90% of the viewport — 12 columns don't breathe at 1200px.
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '90vw',
            // On narrow screens the 12 columns can't fit; let the grid scroll
            // horizontally inside the Paper instead of breaking page layout.
            overflowX: 'auto',
          }}
        >
          <Box sx={{ minWidth: { xs: 720, sm: 'auto' } }}>
            <WafDataGrid<Row>
            rows={filtered}
            columns={columns}
            loading={loading}
            density="standard"
            checkboxSelection={canEdit}
            rowSelectionModel={selection}
            onRowSelectionModelChange={setSelection}
            onRowClick={(p) => navigate(`/entry/${p.row.nomination_uuid}`)}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
            />
          </Box>
        </Paper>
      ) : (
        <>
        <Box
          sx={{
            // Break out of the Layout's 1200px max-width wrapper for the grid
            // only. position:relative + 100vw width + negative left offset is
            // the most reliable cross-browser pattern; the negative-margin
            // shorthand was being overridden by the parent Stack's align-items.
            // overflow-x: clip on the outer Layout Box catches scrollbar slop.
            position: 'relative',
            width: '100vw',
            left: 'calc(-50vw + 50%)',
            px: { xs: 2, sm: 4 },
            boxSizing: 'border-box',
            display: 'grid',
            // Single column on phones; auto-fill larger cards from sm up so the
            // desktop multi-column layout is unchanged.
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fill, minmax(420px, 1fr))',
            },
            gap: 2,
          }}
        >
          {filtered.slice(page * pageSize, page * pageSize + pageSize).map((r) => (
            <EntryCard key={r.id} row={r} onClick={() => navigate(`/entry/${r.nomination_uuid}`)} />
          ))}
          {filtered.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
              No entries match your filters.
            </Typography>
          )}
        </Box>
        {filtered.length > 0 && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            labelRowsPerPage="Per page"
          />
        )}
        </>
      )}

      {canEdit && selection.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
              {selection.length} selected
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => { setDeleteError(null); setDeleteDialog(true); }}
            >
              Delete
            </Button>
          </Stack>
        </Paper>
      )}

      <Dialog open={deleteDialog} onClose={() => !deleting && setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete {selection.length} entr{selection.length === 1 ? 'y' : 'ies'}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Pick how to delete. Soft delete is reversible (row kept, hidden from views);
            hard delete removes the row and its image records permanently.
          </DialogContentText>
          <RadioGroup value={deleteMode} onChange={(e) => setDeleteMode(e.target.value as 'soft' | 'hard')}>
            <FormControlLabel
              value="soft"
              control={<Radio />}
              label={<><strong>Soft delete</strong> — mark as deleted, can be restored later</>}
            />
            <FormControlLabel
              value="hard"
              control={<Radio />}
              label={<><strong>Hard delete</strong> — permanent, also removes image records</>}
            />
          </RadioGroup>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={onConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : `Delete (${deleteMode})`}
          </Button>
        </DialogActions>
      </Dialog>

      <EntryFormDialog
        open={formOpen}
        source={formSource}
        onClose={() => setFormOpen(false)}
        onCreated={(created) => {
          setFormOpen(false);
          navigate(`/entry/${created.nomination_uuid}`);
        }}
      />
    </Stack>
  );
}

// Card tile for the grid view. Hero image fills the top, with a yellow banner
// carrying project + architect metadata below it. Prefers the locally-compressed
// version stored by the import pipeline; falls back to the raw Evessio URL
// if the local file isn't present, and shows a placeholder icon if both fail.
function EntryCard({ row, onClick }: { row: Row; onClick: () => void }) {
  // The backend supplies a ready-to-use hero URL in row.hero_image — a presigned
  // S3 URL, or the static path with a ?v= cache-bust on the filesystem. If it
  // fails to load, fall back to the placeholder.
  const [imgSrc, setImgSrc] = useState<string | null>(row.hero_image || null);
  const [loaded, setLoaded] = useState(false);

  const onImgError = () => setImgSrc(null);

  return (
    <Paper
      onClick={onClick}
      sx={(t) => ({
        cursor: 'pointer',
        overflow: 'hidden',
        display: 'block',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: t.palette.mode === 'dark'
            ? '0 4px 12px rgba(0,0,0,0.6)'
            : '0 4px 12px rgba(0,0,0,0.12)',
        },
      })}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%', // 9 / 16 — enforces 16:9 box regardless of child
          backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.200',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {imgSrc ? (
            <Box
              component="img"
              src={imgSrc}
              onError={onImgError}
              onLoad={() => setLoaded(true)}
              alt={row.project_title || ''}
              loading="lazy"
              decoding="async"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
                opacity: loaded ? 1 : 0,
                transition: 'opacity 200ms ease',
              }}
            />
          ) : (
            <Stack alignItems="center" spacing={0.5} sx={{ color: 'text.disabled' }}>
              <ImageNotSupportedOutlinedIcon fontSize="large" />
              <Typography variant="caption">no image</Typography>
            </Stack>
          )}
        </Box>
        {/* Per-card spinner shown over the image area until the hero loads. */}
        {imgSrc && !loaded && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={28} sx={{ color: WAF_YELLOW }} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: WAF_YELLOW,
          px: 1.5,
          py: 1,
          color: '#111827',
          height: 80,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'rgba(17,24,39,0.75)',
            fontWeight: 600,
            fontSize: 10.5,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={row.category_name || ''}
        >
          {row.category_name || '—'}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            lineHeight: 1.15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={row.project_title || row.nomination_code}
        >
          {row.project_title || row.nomination_code || '(untitled)'}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'rgba(17,24,39,0.7)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.architect_company_name || '—'}
        </Typography>
      </Box>
    </Paper>
  );
}
